import React, { Fragment, useState } from 'react';
import { useTranslate, required, SelectInput } from 'react-admin';
import { Solution } from '~/types';
import {
  KycLevelInput,
  ReferenceInput,
  HiddenInput,
  AutocompleteArrayTextInput,
} from '~/components/ra';
import { useExternalSourceTypes } from '~/hooks/externalSources';
import { useGetAllProfileTags } from '~/hooks/profileTags';
import { Skeleton } from '@material-ui/lab';
import { PIAvailability } from '~/types/solutions';

export const l = (key: string): string =>
  `resources.solutions.paymentInstrumentAvailabilityRules.${key}`;

interface Props {
  record: PIAvailability | null;
  solutionRecord: Solution;
  edit?: boolean;
  create?: boolean;
}

const PIAvailabilityForm = (props: Props) => {
  const { record, solutionRecord, edit, create } = props;
  const initialValue = create ? '' : record?.productId ? 'product' : 'externalSourceType';
  const [instrument, setInstrument] = useState(initialValue);
  const handleInstrumentChange = (e: any) => {
    setInstrument(e.target.value);
  };
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));

  const { data: externalSourceTypesData, isLoading } = useExternalSourceTypes();
  const externalSourceTypes = isLoading
    ? []
    : externalSourceTypesData?.map((type) => ({ id: type, name: type }));

  const { data: allTags, isLoading: allTagsLoading } = useGetAllProfileTags();
  const autocompleteOptions = allTags?.map((tag) => tag?.name);

  return (
    <Fragment>
      {edit && <HiddenInput source='id' disabled />}
      <HiddenInput source='solutionId' disabled />
      <SelectInput
        initialValue={initialValue}
        source='paymentInstrument'
        label={t('paymentInstrument')}
        onChange={handleInstrumentChange}
        choices={[
          { id: 'product', name: t('product') },
          { id: 'externalSourceType', name: t('externalSourceType') },
        ]}
        fullWidth
        validate={required()}
      />
      {instrument === 'product' && (
        <ReferenceInput
          label={t('product')}
          source='productId'
          reference='products'
          filter={{ solutionId: solutionRecord?.id }}
        >
          <SelectInput optionText='name' validate={required()} emptyText={t('noProducts')} />
        </ReferenceInput>
      )}
      {instrument === 'externalSourceType' && (
        <SelectInput
          source='externalSourceType'
          label={t('externalSourceType')}
          choices={externalSourceTypes}
          fullWidth
          validate={required()}
        />
      )}
      <KycLevelInput source='kycLevel' label={t('profileKycLevel')} fullWidth allowEmpty />
      {allTagsLoading ? (
        <Skeleton width='100%' />
      ) : (
        <AutocompleteArrayTextInput
          record={record}
          source='allowedProfileTags'
          label={t('allowedProfileTags')}
          options={autocompleteOptions}
          freeSolo={false}
        />
      )}
      {allTagsLoading ? (
        <Skeleton width='100%' />
      ) : (
        <AutocompleteArrayTextInput
          record={record}
          source='disallowedProfileTags'
          label={t('disallowedProfileTags')}
          options={autocompleteOptions}
          freeSolo={false}
        />
      )}
    </Fragment>
  );
};

export default PIAvailabilityForm;
